<template>
    <form class="form-signin" @submit.prevent="login" data-test-id="form-login">
        <div class="login-card">
            <header class="login-header">
                <img class="login-logo" width="200" src="/images/logo-lead.png" :alt="themeBrandName" />
                <div class="login-heading-row">
                    <div>
                        <h5 class="login-title" data-test-id="title-login_page">
                            {{ $tl(`labels.form.login.${isYservices ? "loginWith" : "login"}`) }}
                        </h5>
                    </div>
                    <ChooseLang class="login-lang" />
                </div>
            </header>
            <loader-component v-if="loading" class="login-loader" />
            <div v-if="errorMessage" class="alert alert-danger login-alert" role="alert" aria-live="assertive">
                {{ errorMessage }}
            </div>
            <template v-if="!loading">
                <template v-if="isYservices">
                    <a class="yservices-login-link" href="https://admin.yproximite.fr/y-services/autologin">
                        <img src="/images/logo-yprox.png" alt="Log with Yproximité" />
                    </a>
                    <div class="login-separator">
                        <hr />
                        <span>{{ $tl("labels.form.login.useMyEmail") }}</span>
                        <hr />
                    </div>
                </template>
                <div class="login-fields">
                    <div class="login-field">
                        <label for="email" class="form-label login-label">{{ $tl("labels.form.email") }}</label>
                        <div class="input-group login-input-group" :class="{ 'is-invalid': hasErrors('email') }">
                            <span class="input-group-text">
                                <feather type="user" />
                            </span>
                            <input
                                id="email"
                                data-test-id="input-txt-email"
                                type="email"
                                class="form-control"
                                :class="{ 'is-invalid': hasErrors('email') }"
                                name="email"
                                v-model="email"
                                @input="clearErrors('email')"
                                :placeholder="$t('labels.form.email')"
                                autocomplete="email"
                                required
                                autofocus />
                            <div
                                data-test-id="errors-email"
                                v-if="hasErrors('email')"
                                class="invalid-feedback"
                                role="alert"
                                v-html="joinErrors('email')"></div>
                        </div>
                    </div>

                    <div class="login-field">
                        <label for="password" class="form-label login-label">{{ $tl("labels.form.password") }}</label>
                        <div class="input-group login-input-group" :class="{ 'is-invalid': hasErrors('password') }">
                            <span class="input-group-text">
                                <feather type="lock" />
                            </span>

                            <input
                                id="password"
                                type="password"
                                data-test-id="input-txt-password"
                                class="form-control"
                                name="password"
                                :class="{ 'is-invalid': hasErrors('password') }"
                                v-model="password"
                                @input="clearErrors('password')"
                                :placeholder="$t('labels.form.password')"
                                autocomplete="current-password"
                                required />
                            <div v-if="hasErrors('password')" class="invalid-feedback" role="alert" v-html="joinErrors('password')"></div>
                        </div>
                    </div>
                </div>

                <div class="login-options">
                    <label class="form-label login-checkbox" for="remember">
                        <input data-test-id="input-chk-remember" type="checkbox" name="remember" id="remember" v-model="remember" />
                        <span class="checkmark"></span>
                        <span>{{ $tl("labels.form.login.rememberMe") }}</span>
                    </label>
                    <a class="login-forgot-link" href="/password/reset">
                        {{ $tl("labels.form.login.forgotPassword") }}
                    </a>
                </div>

                <div v-if="captchaRequired" class="login-captcha">
                    <div ref="turnstile"></div>
                    <div v-if="captchaError" id="login-captcha-error" class="text-danger small mt-2" role="alert" aria-live="polite">
                        {{ captchaError }}
                    </div>
                </div>

                <button
                    data-test-id="btn-login"
                    type="submit"
                    class="btn btn-success btn-circle login-submit"
                    :disabled="captchaRequired && !captchaToken"
                    :aria-describedby="captchaError ? 'login-captcha-error' : null">
                    {{ $tl("labels.form.login.button") }}
                </button>

                <div class="login-register-link">
                    <span>{{ $tl("labels.form.login.noAccount") }}</span>
                    <router-link data-test-id="lnk-register_page" :to="{ name: 'public.auth.register' }">
                        {{ $tl("labels.form.login.goToRegisterPage") }}
                    </router-link>
                </div>

                <footer class="content-footer login-footer">
                    <small class="text-muted"> ©{{ displayDate(null, "y") }} </small>
                </footer>
            </template>
        </div>
    </form>
</template>

<script>
import LoaderComponent from "../../components/LoaderComponent";
import ChooseLang from "../../components/Default/ChooseLang.vue";

export default {
    name: "AuthLogin",
    data() {
        return {
            loading: false,
            errorMessage: null,
            errors: null,
            email: "",
            password: "",
            remember: false,
            captchaRequired: false,
            captchaToken: null,
            captchaError: null,
            captchaProvider: null,
            captchaField: "captcha_token",
            captchaSiteKey: null,
            captchaRetryTimeout: null,
            turnstileWidgetId: null,
            turnstileScriptPromise: null,
        };
    },
    created() {
        if (this.$route.query.auth_error === "admin_domain_required") {
            this.errorMessage = this.$tl("labels.form.login.adminDomainRequired");
        }
    },
    methods: {
        login() {
            this.loading = true;
            this.errors = null;
            this.errorMessage = null;

            let redirect = this.$route.query.redirect;

            this.$store
                .dispatch("auth/login", {
                    email: this.email,
                    password: this.password,
                    remember: this.remember,
                    redirect,
                    ...(this.captchaRequired && this.captchaToken ? { [this.captchaField]: this.captchaToken } : {}),
                })
                .then((response) => {
                    if (response && response.data && response.data.redirect_url) {
                        window.location.href = response.data.redirect_url;
                        return;
                    }
                    window.location.href = this.safeRedirectPath(redirect);
                })
                .catch((error) => {
                    if (error.request && error.request.status && error.request.responseURL) {
                        const codes = [404];
                        if (codes.includes(error.request.status)) {
                            document.location.href = error.request.responseURL;
                            return;
                        }
                    }
                    if (error.response && error.response.data.message) {
                        this.errorMessage = error.response.data.message;
                    }

                    if (error.response && error.response.data.errors) {
                        this.errors = error.response.data.errors;
                    } else if (error.message) {
                        this.errorMessage = error.message;
                    }
                    const responseData = error.response && error.response.data ? error.response.data : {};
                    this.captchaRequired = Boolean(responseData.captcha_required);
                    this.configureCaptcha(responseData.captcha || null);
                    this.resetCaptchaToken();
                    this.destroyTurnstile();
                    this.password = "";
                    this.loading = false;

                    if (this.captchaRequired) {
                        this.$nextTick(() => this.renderTurnstile());
                    }
                });
        },
        safeRedirectPath(redirect) {
            if (typeof redirect !== "string") {
                return "/";
            }

            const trimmedRedirect = redirect.trim();
            if (!trimmedRedirect.startsWith("/") || trimmedRedirect.startsWith("//") || trimmedRedirect.includes("\\")) {
                return "/";
            }

            return trimmedRedirect;
        },
        configureCaptcha(captcha) {
            this.captchaProvider = captcha && captcha.provider ? captcha.provider : "turnstile";
            this.captchaField = captcha && captcha.field ? captcha.field : "captcha_token";
            this.captchaSiteKey = captcha && captcha.site_key ? captcha.site_key : this.captchaSiteKey;
        },
        loadTurnstileScript() {
            if (window.turnstile) {
                return Promise.resolve();
            }

            if (this.turnstileScriptPromise) {
                return this.turnstileScriptPromise;
            }

            this.turnstileScriptPromise = new Promise((resolve, reject) => {
                const script = document.createElement("script");
                script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
                script.async = true;
                script.defer = true;
                script.onload = resolve;
                script.onerror = () => {
                    script.remove();
                    this.turnstileScriptPromise = null;
                    reject(new Error("Turnstile script failed to load"));
                };
                document.head.appendChild(script);
            });

            return this.turnstileScriptPromise;
        },
        renderTurnstile() {
            if (!this.captchaRequired || !this.$refs.turnstile) {
                return;
            }

            if (this.captchaProvider !== "turnstile" || !this.captchaSiteKey) {
                this.captchaError = this.$tl("labels.form.login.captchaUnavailable");
                return;
            }

            this.loadTurnstileScript()
                .then(() => {
                    if (this.turnstileWidgetId !== null || !this.$refs.turnstile) {
                        return;
                    }

                    this.turnstileWidgetId = window.turnstile.render(this.$refs.turnstile, {
                        sitekey: this.captchaSiteKey,
                        callback: (token) => {
                            this.captchaToken = token;
                            this.captchaError = null;
                        },
                        "expired-callback": () => this.resetCaptchaToken(),
                        "error-callback": () => {
                            this.resetCaptchaToken();
                            this.captchaError = this.$tl("labels.form.login.captchaFailed");
                            this.scheduleTurnstileRetry();
                        },
                    });
                })
                .catch(() => {
                    this.turnstileScriptPromise = null;
                    this.captchaError = this.$tl("labels.form.login.captchaUnavailable");
                    this.scheduleTurnstileRetry();
                });
        },
        scheduleTurnstileRetry() {
            if (this.captchaRetryTimeout !== null) {
                return;
            }

            this.captchaRetryTimeout = window.setTimeout(() => {
                this.captchaRetryTimeout = null;
                this.renderTurnstile();
            }, 2000);
        },
        resetCaptchaToken() {
            this.captchaToken = null;

            if (this.turnstileWidgetId !== null && window.turnstile && this.$refs.turnstile) {
                window.turnstile.reset(this.turnstileWidgetId);
            }
        },
        destroyTurnstile() {
            if (this.captchaRetryTimeout !== null) {
                window.clearTimeout(this.captchaRetryTimeout);
                this.captchaRetryTimeout = null;
            }

            if (this.turnstileWidgetId !== null && window.turnstile) {
                window.turnstile.remove(this.turnstileWidgetId);
            }

            this.turnstileWidgetId = null;
        },
        clearErrors(key) {
            if (!this.errors) return;

            if (this.errors[key]) {
                this.$delete(this.errors, key);
            }

            if (Object.keys(this.errors).length === 0) {
                this.errors = null;
                this.errorMessage = null;
            }
        },
        hasErrors(key) {
            return this.errors !== null && this.errors[key] && this.errors[key].length > 0;
        },
        joinErrors(key) {
            return this.errors[key].join("<br/>");
        },
    },
    beforeDestroy() {
        this.destroyTurnstile();
    },
    components: {
        LoaderComponent,
        ChooseLang,
    },
};
</script>
